import {
  Component,
  input,
  output,
  computed,
  inject,
  signal,
  Input,
  Output,
  EventEmitter
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';
import {
  RespuestaLlamada,
  RespuestaLlamadaRepository
} from 'src/app/domain/service/respuesta-llamada/respuesta-llamada-repository';
import { DireccionCursor } from 'src/app/domain/model/common/page-response.dto';
import { busquedaRespuestaPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BaseBusquedaPaginadaDialog } from 'src/app/presentation/shared/busquedas/base-busqueda-paginada-dialog';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';
import {
  sanitizeText,
  sanitizeTextPreserveSpaces
} from 'src/app/common/input-utils';

export interface FiltroRespuestaLlamada {
  codigo?: string;
  descripcion?: string;
}

export interface RespuestaLlamadaSeleccionada {
  nmconsecutivo: number;
  dsvalor: string;
  descripcionCompleta: string;
  /** S/N -> indica si es respuesta de cliente (esRespuestaCliente). Legacy: snaplica_nueva_llamada */
  snaplicaNuevaLlamada?: string;
  /** Horas para sugerir la nueva gestión. Legacy: nmhoras_nueva_llamada */
  nmhorasNuevaLlamada?: number | null;
}

@Component({
  selector: 'app-busqueda-respuesta-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    ProgressSpinnerModule,
    DropdownModule,
    TooltipModule,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      [header]="titulo() || 'Buscar Respuesta'"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '50rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      (onHide)="onCancelar()"
    >
      <div
        class="border rounded p-4 mb-4 bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
      >
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >Código</label
            >
            <input
              pInputText
              [(ngModel)]="filtro.codigo"
              (input)="sanitizarCampo('codigo')"
              class="w-full"
              maxlength="20"
              (keyup.enter)="buscar()"
            />
          </div>
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >Descripción</label
            >
            <input
              pInputText
              [(ngModel)]="filtro.descripcion"
              (input)="sanitizarCampo('descripcion')"
              class="w-full"
              maxlength="100"
              (keyup.enter)="buscar()"
            />
          </div>
        </div>
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 mt-4 w-full"
        >
          <button
            pButton
            icon="pi pi-search"
            label="Buscar"
            class="p-button-sm min-h-[44px]"
            (click)="buscar()"
            [disabled]="loading()"
          ></button>
          <button
            pButton
            icon="pi pi-eraser"
            label="Limpiar"
            class="p-button-sm p-button-secondary p-button-outlined min-h-[44px]"
            (click)="limpiarFiltros()"
          ></button>
        </div>
      </div>
      @if (mensaje()) {
        <div
          class="p-3 mb-3 rounded"
          [ngClass]="{
            'bg-yellow-100 text-yellow-800': mensajeTipo() === 'warning',
            'bg-blue-100 text-blue-800': mensajeTipo() === 'info',
            'bg-red-100 text-red-800': mensajeTipo() === 'error'
          }"
        >
          <i
            class="pi mr-2"
            [ngClass]="{
              'pi-exclamation-triangle': mensajeTipo() === 'warning',
              'pi-info-circle': mensajeTipo() === 'info',
              'pi-times-circle': mensajeTipo() === 'error'
            }"
          ></i>
          {{ mensaje() }}
        </div>
      }
      @if (loading()) {
        <div class="flex justify-center p-4">
          <p-progressSpinner
            strokeWidth="4"
            [style]="{ width: '50px', height: '50px' }"
          ></p-progressSpinner>
        </div>
      }
      @if (!loading() && items().length > 0) {
        <div
          class="font-medium text-gray-700 dark:text-gray-200 p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm flex justify-between items-center"
        >
          <span>Listado de Respuestas</span>
          @if (totalRegistros() > 0) {
            <span class="text-xs text-gray-500 dark:text-gray-400">
              Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de
              {{ totalRegistros() }}
            </span>
          }
        </div>
        <div class="md:hidden space-y-3 p-3">
          @for (item of items(); track item.nmconsecutivo) {
            <div
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
            >
              <div class="mb-3 min-w-0">
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  Descripción
                </div>
                <div
                  class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                  [attr.title]="tituloTabla(item.dsrespuesta)"
                >
                  {{ textoTabla(item.dsrespuesta) }}
                </div>
              </div>
              <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Código:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(item.nmconsecutivo)
                  }}</span>
                </div>
              </div>
              <p-button
                label="Seleccionar"
                icon="pi pi-check"
                size="small"
                styleClass="w-full min-h-[44px]"
                (onClick)="seleccionarItem(item)"
              />
            </div>
          }
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [resizableColumns]="true"
            columnResizeMode="expand"
            [value]="items()"
            [scrollable]="true"
            [responsiveLayout]="'scroll'"
            [scrollHeight]="tableConfig().responsive.scrollHeight || '300px'"
            [selectionMode]="tableSelectionMode()"
            [(selection)]="seleccionado"
            (onRowSelect)="onRowSelect($event)"
            [tableStyle]="{
              'min-width': tableConfig().responsive.minWidth || 'max-content'
            }"
            [dataKey]="
              (tableConfig().selection.dataKey || 'nmconsecutivo').toString()
            "
            [styleClass]="tableStyleClass()"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Código">Código</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Descripción"
                    >Descripción</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Seleccionar"
                    >Seleccionar</span
                  >
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-item>
              <tr [pSelectableRow]="item">
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.nmconsecutivo)"
                    >{{ textoTabla(item.nmconsecutivo) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.dsrespuesta)"
                    >{{ textoTabla(item.dsrespuesta) }}</span
                  >
                </td>
                <td>
                  <button
                    pButton
                    label="Seleccionar"
                    class="p-button-sm p-button-text"
                    (click)="seleccionarItem(item)"
                  ></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
        <app-busqueda-paginador
          [currentPage]="paginaActual()"
          [totalPages]="totalPaginas()"
          [hasPrevious]="hasPrevious()"
          [hasNext]="hasNext()"
          [pageSizeOptions]="pageSizeOptions"
          [selectedPageSize]="selectedPageSize"
          (pageSizeChange)="onSelectedPageSizeChange($event)"
          (first)="irPrimeraPagina()"
          (previous)="loadPreviousPage()"
          (next)="loadNextPage()"
          (last)="irUltimaPagina()"
        ></app-busqueda-paginador>
      }
      @if (!loading() && busquedaRealizada && items().length === 0) {
        <div class="text-center text-gray-500 p-4 border rounded">
          No hay datos
        </div>
      }
      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cerrar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="onCancelar()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class BusquedaRespuestaDialogComponent extends BaseBusquedaPaginadaDialog<RespuestaLlamada> {
  private readonly respuestaLlamadaRepository = inject(
    RespuestaLlamadaRepository
  );
  titulo = input<string>('');
  onSelect = output<RespuestaLlamadaSeleccionada>();
  onClose = output<void>();

  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  filtro: FiltroRespuestaLlamada = {};

  /** Sanitización XSS/SQLi (seguridad-girop §3) para filtros de texto libre */
  sanitizeText = sanitizeText;
  seleccionado: RespuestaLlamada | null = null;

  // Flags de navegación: los fija la RESPUESTA del backend (frontend-girop §17.7).
  // Sustituyen a los computed de la base paginada (que derivan de paginaActual).
  override hasNext = signal(false);
  override hasPrevious = signal(false);

  /** Cursores opacos de la última respuesta: se reenvían tal cual. */
  private nextCursorActual: string | null = null;
  private previousCursorActual: string | null = null;

  /** Texto de la búsqueda vigente; la navegación lo reutiliza con su cursor. */
  private filtroAplicado = '';

  readonly tableConfig = computed(() =>
    busquedaRespuestaPreset.createConfig({
      selection: {
        mode: 'single',
        dataKey: 'nmconsecutivo',
        selected: this.seleccionado as unknown as Record<string, unknown> | null
      },
      style: {
        tableClassName: 'tabla-busqueda-respuesta',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'No hay datos'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: 'max-content',
        scrollHeight: '300px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de busqueda de respuestas',
        announceSelection: true,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = this.buildTableStyleClass(this.tableConfig);

  readonly tableSelectionMode = this.buildTableSelectionMode(this.tableConfig);

  /**
   * Construye el texto de búsqueda a partir de los filtros.
   */
  private construirTextoBusqueda(): string {
    const partes: string[] = [];
    if (this.filtro.codigo?.trim()) {
      partes.push(this.filtro.codigo.trim());
    }
    if (this.filtro.descripcion?.trim()) {
      partes.push(this.filtro.descripcion.trim());
    }
    return partes.join(' ');
  }

  /**
   * Ejecuta una búsqueda nueva (siempre desde la página 1).
   */
  async buscar(): Promise<void> {
    this.sanitizarCampo('codigo', true);
    this.sanitizarCampo('descripcion', true);
    this.mensaje.set('');
    this.busquedaRealizada = true;
    this.filtroAplicado = this.construirTextoBusqueda();
    await this.cargarPagina(null, 'NEXT', 1);
  }

  /**
   * Sanitización XSS de texto libre (seguridad-girop §3). Mientras se teclea
   * conserva los espacios; antes de buscar también los recorta.
   */
  sanitizarCampo(campo: 'codigo' | 'descripcion', recortar = false): void {
    const valor = this.filtro[campo];
    if (valor === undefined) return;
    this.filtro[campo] = recortar
      ? sanitizeText(valor)
      : sanitizeTextPreserveSpaces(valor);
  }

  /** Cambio de tamaño de página (base): vuelve a la página 1 de la búsqueda vigente. */
  protected async ejecutarBusqueda(_start: number): Promise<void> {
    await this.cargarPagina(null, 'NEXT', 1);
  }

  override async irPrimeraPagina(): Promise<void> {
    if (this.loading()) return;
    await this.cargarPagina(null, 'NEXT', 1);
  }

  override async loadPreviousPage(): Promise<void> {
    if (this.loading() || !this.hasPrevious()) return;
    await this.cargarPagina(
      this.previousCursorActual,
      'PREVIOUS',
      Math.max(1, this.paginaActual() - 1)
    );
  }

  override async loadNextPage(): Promise<void> {
    if (this.loading() || !this.hasNext()) return;
    await this.cargarPagina(this.nextCursorActual, 'NEXT', this.paginaActual() + 1);
  }

  /** Última página: direction=LAST con cursor nulo; la página se calcula con la respuesta. */
  override async irUltimaPagina(): Promise<void> {
    if (this.loading() || this.totalRegistros() === 0) return;
    await this.cargarPagina(null, 'LAST', 1);
  }

  /**
   * Pide una página al backend con el filtro de la búsqueda vigente (frontend-girop
   * §17.7). Página, cursores y flags solo cambian con una respuesta exitosa.
   */
  private async cargarPagina(
    cursor: string | null,
    direction: DireccionCursor,
    pagina: number
  ): Promise<void> {
    const size = this.pageSize();
    this.loading.set(true);
    this.mensaje.set('');
    try {
      const response = await this.respuestaLlamadaRepository.buscarPagina(
        this.filtroAplicado,
        cursor,
        size,
        direction
      );
      const content = response?.content ?? [];
      const total = response?.totalElements ?? 0;
      this.items.set(content);
      this.totalRegistros.set(total);
      this.nextCursorActual = response?.nextCursor ?? null;
      this.previousCursorActual = response?.previousCursor ?? null;
      this.hasNext.set(response?.hasNext ?? false);
      this.hasPrevious.set(response?.hasPrevious ?? false);
      this.paginaActual.set(
        direction === 'LAST'
          ? size > 0 && total > 0
            ? Math.max(1, Math.ceil(total / size))
            : 1
          : pagina
      );
      if (content.length === 0) {
        this.mensaje.set('No se encontraron resultados');
        this.mensajeTipo.set('warning');
      }
    } catch (error) {
      this.mensaje.set(
        this.resolverMensajeErrorBusqueda(error, 'Error al buscar')
      );
      this.mensajeTipo.set('error');
      this.items.set([]);
      this.totalRegistros.set(0);
      this.hasNext.set(false);
      this.hasPrevious.set(false);
    } finally {
      this.loading.set(false);
    }
  }

  limpiarFiltros(): void {
    this.filtro = {};
    this.filtroAplicado = '';
    this.items.set([]);
    this.clearMensajes();
    this.resetPaginacion();
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.nextCursorActual = null;
    this.previousCursorActual = null;
    // NO llamamos buscar() aquí, igual que tareas
  }

  onRowSelect(event: any): void {
    if (event?.data) {
      this.seleccionado = event.data as RespuestaLlamada;
    }
  }

  // Cuando el usuario cierra el modal (por botón o X)
  onDialogHide() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.onClose.emit();
  }

  seleccionarItem(item: RespuestaLlamada): void {
    // Formato: "NMCONSECUTIVO - DSRESPUESTA"
    const descripcionCompleta = `${item.nmconsecutivo} - ${item.dsrespuesta}`;
    const resultado: RespuestaLlamadaSeleccionada = {
      nmconsecutivo: item.nmconsecutivo,
      dsvalor: item.dsrespuesta,
      descripcionCompleta,
      snaplicaNuevaLlamada: item.snAplicaNuevaLlamada,
      nmhorasNuevaLlamada: item.nmhorasNuevaLlamada
    };
    this.onSelect.emit(resultado);
    this.visible = false;
    this.visibleChange.emit(false);
    this.limpiarFiltros();
  }

  onCancelar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.onClose.emit();
    this.limpiarFiltros();
  }

}
